import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, Reorder, useDragControls, AnimatePresence } from "motion/react";

import useWizard from "../useWizard";
import { ACCENT_COLOR } from "../theme";
import { WizardToggle, CustomFieldEditor } from "../parts";

function GeneralInfoPane() {
  const { data, updateField } = useWizard();
  const info = data.studentInfo;

  return (
    <Content>
      <div>
        <Title className="merri">General Info</Title>
        <Subtitle className="raleway">
          We'll collect basic information on each student and family. Uncheck anything you'd like us to not to ask them. We'll give you a spot to ask for other custom information soon.
        </Subtitle>
      </div>

      <FormCenter>
      <ToggleList>
        <WizardToggle
          label="Address"
          icon="fa-location-dot"
          checked={info.address}
          onChange={(v) => updateField("studentInfo", "address", v)}
          alt={false}
        />
        <WizardToggle
          label="Birthday"
          icon="fa-cake-candles"
          checked={info.birthday}
          onChange={(v) => updateField("studentInfo", "birthday", v)}
          alt
        />
        <WizardToggle
          label="Gender"
          icon="fa-venus-mars"
          checked={info.gender}
          onChange={(v) => updateField("studentInfo", "gender", v)}
          alt={false}
        />
        <WizardToggle
          label="School"
          icon="fa-school"
          checked={info.school}
          onChange={(v) => updateField("studentInfo", "school", v)}
          alt
        />
        <WizardToggle
          label="Grade"
          icon="fa-graduation-cap"
          checked={info.grade}
          onChange={(v) => updateField("studentInfo", "grade", v)}
          alt={false}
        />
        <WizardToggle
          label="Shirt Size"
          icon="fa-shirt"
          checked={info.shirtSize}
          onChange={(v) => updateField("studentInfo", "shirtSize", v)}
          alt
          last
        />
      </ToggleList>
      </FormCenter>
    </Content>
  );
}

function BaptismPane() {
  const { data, updateField, nextSubStep } = useWizard();
  const info = data.studentInfo;

  const handleYes = () => {
    updateField("studentInfo", "baptism", true);
    if (info.baptism === true) {
      nextSubStep();
    } else {
      setTimeout(() => nextSubStep(), 300);
    }
  };

  const handleNo = () => {
    updateField("studentInfo", "baptism", false);
    if (info.baptism === false) {
      nextSubStep();
    } else {
      setTimeout(() => nextSubStep(), 300);
    }
  };

  return (
    <Content>
      <div>
        <Title className="merri">Baptism Info</Title>
        <Subtitle className="raleway">
          Would you like us to ask registrants if they are baptized or not? If they are, we'll give them the option to upload a copy of their baptismal certificate if it's not on file.
        </Subtitle>
      </div>

      <FormCenter>
        <ChoiceRows>
          <ChoiceRow onClick={handleYes} $selected={info.baptism === true}>
            <ChoiceRowInner>
              <ChoiceIcon className="fas fa-water" />
              <ChoiceLabel>Yes, ask if they are baptized.</ChoiceLabel>
            </ChoiceRowInner>
            <CheckIcon className="fas fa-circle-check" $visible={info.baptism === true} />
          </ChoiceRow>
          <ChoiceRow onClick={handleNo} $selected={info.baptism === false}>
            <ChoiceRowInner>
              <ChoiceIcon className="fas fa-xmark" />
              <ChoiceLabel>No, skip this.</ChoiceLabel>
            </ChoiceRowInner>
            <CheckIcon className="fas fa-circle-check" $visible={info.baptism === false} />
          </ChoiceRow>
        </ChoiceRows>
      </FormCenter>
    </Content>
  );
}

function MedicalEmergencyPane() {
  const { data, updateField } = useWizard();
  const info = data.studentInfo;

  return (
    <Content>
      <div>
        <Title className="merri">Medical & Emergency</Title>
        <Subtitle className="raleway">
          Would you like to collect medical information and emergency contacts for each student? Toggle on the fields you'd like to include.
        </Subtitle>
      </div>

      <FormCenter>
      <ToggleList>
        <WizardToggle
          label="Allergies & Medical Conditions"
          description="Ask about students' food allergies or medical conditions."
          icon="fa-heart-pulse"
          checked={info.allergiesMedical}
          onChange={(v) => updateField("studentInfo", "allergiesMedical", v)}
          alt={false}
        />
        <WizardToggle
          label="Emergency Contacts"
          description="Parents with phone numbers are automatically added as emergency contacts. Check this box to ask them to add an additional emergency contact."
          icon="fa-phone"
          checked={info.emergencyContacts}
          onChange={(v) => updateField("studentInfo", "emergencyContacts", v)}
          alt
          last
        />
      </ToggleList>
      </FormCenter>
    </Content>
  );
}

function joinWords(words) {
  if (words.length === 0) return "";
  if (words.length === 1) return words[0];
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  return words.slice(0, -1).join(", ") + ", and " + words[words.length - 1];
}

function useSummarySections() {
  const { data } = useWizard();
  const info = data.studentInfo;
  const perm = data.permissionRelease;

  const sections = [];

  // Always collected
  const familyParts = ["name", "email", "phone number"];
  if (info.address) familyParts.push("home address");
  sections.push({
    icon: "fa-users",
    title: "Family Info",
    summary: `We'll collect each student's name along with their parent or guardian's ${joinWords(familyParts)}.`,
  });

  // Student detail toggles
  const details = [];
  if (info.birthday) details.push("birthday");
  if (info.gender) details.push("gender");
  if (info.school) details.push("school");
  if (info.grade) details.push("grade");
  if (info.shirtSize) details.push("shirt size");
  if (details.length > 0) {
    sections.push({
      icon: "fa-id-card",
      title: "Student Details",
      summary: `We'll also ask for each student's ${joinWords(details)}.`,
    });
  }

  // Baptism
  if (info.baptism) {
    sections.push({
      icon: "fa-water",
      title: "Baptism",
      summary: "We'll ask if the student has been baptized and give them the option to upload a certificate.",
    });
  }

  // Permission releases
  const releases = [];
  if (perm.generalReleaseCustom && !perm.generalReleaseDeclined) releases.push("general permission release");
  if (perm.photoReleaseCustom && !perm.photoReleaseDeclined) releases.push("photo release");
  if (releases.length > 0) {
    sections.push({
      icon: "fa-clipboard-check",
      title: "Permission Forms",
      summary: `Families will be asked to agree to your ${joinWords(releases)}.`,
    });
  }

  // Medical & Emergency
  const medParts = [];
  if (info.allergiesMedical) medParts.push("any allergies or medical conditions");
  if (info.emergencyContacts) medParts.push("an additional emergency contact");
  if (medParts.length > 0) {
    sections.push({
      icon: "fa-heart-pulse",
      title: "Medical & Emergency",
      summary: `We'll ask families to provide ${joinWords(medParts)} for their students.`,
    });
  }

  // More Info toggles
  const moreParts = [];
  if (info.previousParish) moreParts.push("previous parish");
  if (info.preferredLanguage) moreParts.push("preferred language");
  if (info.godparents) moreParts.push("godparents/sponsors");
  if (info.siblings) moreParts.push("siblings in program");
  if (info.specialNeeds) moreParts.push("special needs/accommodations");
  if (info.parentVolunteer) moreParts.push("parent volunteer interest");
  if (info.massAttendance) moreParts.push("mass attendance");
  if (info.transportationNeeds) moreParts.push("transportation needs");

  // Custom fields
  const customFieldLabels = (info.customFields || []).filter(f => f.label.trim()).map(f => f.label);
  moreParts.push(...customFieldLabels);

  if (moreParts.length > 0) {
    sections.push({
      icon: "fa-pen-field",
      title: "More Info",
      summary: `We'll also ask about ${joinWords(moreParts)}.`,
    });
  }

  return sections;
}

function SummaryView() {
  const { goToStep, setNextOverride } = useWizard();
  const sections = useSummarySections();
  const hasMoreInfo = sections.some(s => s.title === "More Info");

  useEffect(() => {
    setNextOverride(() => {
      goToStep(4, 0, true);
      return true;
    });
    return () => setNextOverride(null);
  }, [setNextOverride, goToStep]);

  return (
    <Content>
      <div>
        <Title className="merri">Signup Summary</Title>
        <Subtitle className="raleway">
          We've got everything we need before we get to pricing. Here's a summary of what we'll ask families when signing up for your RE program.
        </Subtitle>
      </div>

      <SummaryCenter>
        <SummaryList>
          {sections.map((section) => (
            <SummaryRow key={section.title}>
              <SummaryIconWrap>
                <SummaryIcon className={`fas ${section.icon}`} />
              </SummaryIconWrap>
              <SummaryText>
                <SummaryTitle>{section.title}</SummaryTitle>
                <SummarySentence>{section.summary}</SummarySentence>
              </SummaryText>
              {section.title === "More Info" && (
                <EditButton onClick={() => goToStep(3, 4)}>
                  <EditButtonInner>
                    <i className="fas fa-pen" /> Edit
                  </EditButtonInner>
                </EditButton>
              )}
            </SummaryRow>
          ))}

          {!hasMoreInfo && (
            <AddMoreWrap>
              <AddMoreButton onClick={() => goToStep(3, 4)}>
                <i className="fas fa-plus" /> I need to ask for more information
              </AddMoreButton>
            </AddMoreWrap>
          )}
        </SummaryList>
      </SummaryCenter>
    </Content>
  );
}

const TYPE_LABELS = {
  text: "Text",
  number: "Number",
  address: "Address",
  date: "Date",
  select: "Select",
  file: "File",
};

const TYPE_DEFAULT_ICONS = {
  text: "fa-font",
  number: "fa-hashtag",
  address: "fa-location-dot",
  date: "fa-calendar-days",
  select: "fa-list-check",
  file: "fa-cloud-arrow-up",
};

function CFModePillSlider({ mode }) {
  const hasAnimated = useRef(false);
  const isRequire = mode === "require";
  const t = hasAnimated.current
    ? { duration: 0.2, ease: [0.4, 0, 0.2, 1] }
    : { duration: 0 };

  useEffect(() => { hasAnimated.current = true; }, []);

  return (
    <CFSliderWrap>
      <motion.span animate={{ y: isRequire ? -22 : 0 }} transition={t}>
        Optional
      </motion.span>
      <motion.span animate={{ y: isRequire ? 0 : 22 }} transition={t}>
        Required
      </motion.span>
    </CFSliderWrap>
  );
}

function CustomFieldItem({ item, total, onEdit, onRemove, onDragStart: onDragStartParent, onDragEnd, layoutTransition, mode, onModeChange }) {
  const dragControls = useDragControls();
  const [dragging, setDragging] = useState(false);
  const [dragDir, setDragDir] = useState(-1);
  const [deleteReady, setDeleteReady] = useState(false);
  const [pressing, setPressing] = useState(false);
  const deleteTimerRef = useRef(null);
  const didDragRef = useRef(false);
  const fieldIcon = item.icon || TYPE_DEFAULT_ICONS[item.type] || "fa-font";

  const handleMouseEnter = () => {
    deleteTimerRef.current = setTimeout(() => setDeleteReady(true), 400);
  };
  const handleMouseLeave = () => {
    clearTimeout(deleteTimerRef.current);
    setDeleteReady(false);
    setPressing(false);
  };
  const handleMouseDown = (e) => {
    if (e.target.closest("button") || e.target.closest("[data-drag-handle]")) return;
    setPressing(true);
  };
  const handleMouseUp = () => setPressing(false);

  const handleClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    onEdit();
  };

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={dragControls}
      layout="position"
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ layout: layoutTransition, height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.2 } }}
      style={{ listStyle: "none", overflow: dragging ? "visible" : "hidden", position: "relative", zIndex: dragging ? 999 : "auto" }}
      onDragStart={() => { didDragRef.current = true; setDragging(true); setDragDir(-1); onDragStartParent?.(); }}
      onDrag={(e, info) => { if (Math.abs(info.velocity.y) > 5) setDragDir(info.velocity.y > 0 ? 1 : -1); }}
      onDragEnd={() => { setDragging(false); setDragDir(-1); onDragEnd(); }}
    >
      <CFRow $alt={item.displayIndex % 2 === 0} $dragging={dragging} $dragDir={dragDir} $first={item.displayIndex === 0} $pressing={pressing} onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
        <CFWrapArea>
          <CFLabelGroup>
            <CFIconCircle>
              <i className={`fas ${fieldIcon}`} />
            </CFIconCircle>
            <CFLabel>{item.label || "Untitled Field"}</CFLabel>
          </CFLabelGroup>
          <CFActions>
            <CFDeleteButton $ready={deleteReady} onClick={(e) => { e.stopPropagation(); if (deleteReady) onRemove(); }} tabIndex={-1}>
              <i className="fas fa-trash-can" />
            </CFDeleteButton>
            <CFModePill
              onClick={(e) => { e.stopPropagation(); onModeChange(mode === "require" ? "ask" : "require"); }}
              $require={mode === "require"}
            >
              <CFModePillSlider mode={mode === "require" ? "require" : "ask"} />
            </CFModePill>
          </CFActions>
        </CFWrapArea>
        {total > 1 && (
          <CFDragHandle data-drag-handle onPointerDown={(e) => { e.stopPropagation(); dragControls.start(e); }}>
            <i className="fas fa-grip-vertical" />
          </CFDragHandle>
        )}
      </CFRow>
    </Reorder.Item>
  );
}

function CustomFieldsView() {
  const { data, updateField, goToStep, setBackOverride, setNextOverride, directionOverrideRef, setModalOpen } = useWizard();
  const info = data.studentInfo;
  const modes = info.customFieldModes || {};
  const customFields = info.customFields || [];
  const hasProfileFields = true; // TODO: set based on church config

  const [nextId, setNextId] = useState(() => customFields.length);
  const [, forceUpdate] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isDraggingAny, setIsDraggingAny] = useState(false);
  const layoutTransition = useRef({ duration: 0 });

  useEffect(() => {
    const t = setTimeout(() => { layoutTransition.current = { type: "spring", stiffness: 400, damping: 30 }; }, 300);
    return () => clearTimeout(t);
  }, []);

  const itemsRef = useRef(
    customFields.map((f, i) => ({ id: i, label: f.label, type: f.type, icon: f.icon, options: f.options, displayIndex: i }))
  );

  // Sync values from wizard state
  itemsRef.current.forEach((item, i) => {
    if (i < customFields.length) {
      item.label = customFields[i].label;
      item.type = customFields[i].type;
      item.icon = customFields[i].icon;
      item.options = customFields[i].options;
    }
  });

  const items = itemsRef.current;

  const openEditorForNew = () => {
    setEditingIndex(null);
    setEditorOpen(true);
    setModalOpen(true);
  };

  const openEditorForEdit = (index) => {
    setEditingIndex(index);
    setEditorOpen(true);
    setModalOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingIndex(null);
    setModalOpen(false);
  };

  const handleEditorSave = (fieldData) => {
    if (editingIndex !== null) {
      // Update existing
      const next = customFields.map((f, i) =>
        i === editingIndex ? { ...f, ...fieldData } : f
      );
      itemsRef.current[editingIndex] = {
        ...itemsRef.current[editingIndex],
        ...fieldData,
      };
      updateField("studentInfo", "customFields", next);
    } else {
      // Add new
      const newItem = { id: nextId, ...fieldData, displayIndex: items.length };
      itemsRef.current = [...itemsRef.current, newItem];
      setNextId(nextId + 1);
      updateField("studentInfo", "customFields", [...customFields, fieldData]);
    }
    closeEditor();
  };

  const removeCustomField = (index) => {
    itemsRef.current = itemsRef.current.filter((_, i) => i !== index);
    itemsRef.current.forEach((item, i) => { item.displayIndex = i; });
    updateField("studentInfo", "customFields", customFields.filter((_, i) => i !== index));
  };

  const handleReorder = (newItems) => {
    itemsRef.current = newItems;
    updateField("studentInfo", "customFields", newItems.map((it) => ({
      label: it.label, type: it.type, icon: it.icon, options: it.options,
    })));
  };

  const handleDragEnd = () => {
    itemsRef.current.forEach((item, i) => { item.displayIndex = i; });
    forceUpdate(n => n + 1);
  };

  const setMode = (field, mode) => {
    updateField("studentInfo", "customFieldModes", { ...modes, [field]: mode });
  };

  const hasAnySelected = info.previousParish || info.preferredLanguage || info.godparents ||
    info.siblings || info.specialNeeds || info.parentVolunteer ||
    info.massAttendance || info.transportationNeeds;
  const hasCustomFields = customFields.some(f => f.label.trim());

  const editingField = editingIndex !== null ? customFields[editingIndex] : null;

  useEffect(() => {
    const goNext = () => {
      directionOverrideRef.current = true;
      if (hasAnySelected || hasCustomFields) {
        goToStep(3, 3);
      } else {
        goToStep(4, 0, true);
      }
      return true;
    };
    const goBackToSummary = () => {
      goToStep(3, 3);
      return true;
    };
    setNextOverride(goNext);
    setBackOverride(goBackToSummary);
    return () => {
      setNextOverride(null);
      setBackOverride(null);
    };
  }, [setNextOverride, setBackOverride, goToStep, hasAnySelected, hasCustomFields]);

  return (
    <Content>
      <div>
        <Title className="merri">More Info</Title>
        <Subtitle className="raleway">
          Select any fields from your member's profiles to add or create your own custom fields for this RE program.
        </Subtitle>
      </div>

      <CustomFieldEditor
        open={editorOpen}
        onClose={closeEditor}
        onSave={handleEditorSave}
        editingField={editingField}
      />

      <TwoColumnGrid $single={!hasProfileFields}>
        <ColumnLeft $single={!hasProfileFields}>
          <ListTitle>Custom Fields</ListTitle>
          <CustomFieldsSection>
            <CFList $dragging={isDraggingAny}>
              {items.length > 0 && (
                <Reorder.Group
                  axis="y"
                  values={items}
                  onReorder={handleReorder}
                  as="div"
                  style={{ display: "flex", flexDirection: "column", padding: 0 }}
                >
                  <AnimatePresence initial={false}>
                    {items.map((item, i) => (
                      <CustomFieldItem
                        key={item.id}
                        item={item}
                        total={items.length}
                        onEdit={() => openEditorForEdit(i)}
                        onRemove={() => removeCustomField(i)}
                        onDragStart={() => setIsDraggingAny(true)}
                        onDragEnd={() => { setIsDraggingAny(false); handleDragEnd(); }}
                        layoutTransition={layoutTransition.current}
                        mode={modes[`cf_${item.id}`]}
                        onModeChange={(v) => setMode(`cf_${item.id}`, v)}
                      />
                    ))}
                  </AnimatePresence>
                </Reorder.Group>
              )}
              <CFAddRow $alt={items.length % 2 === 0} onClick={openEditorForNew}>
                <CFAddInner>
                  <i className="fas fa-circle-plus" /> Add Custom Field
                </CFAddInner>
              </CFAddRow>
            </CFList>
          </CustomFieldsSection>
        </ColumnLeft>

        {hasProfileFields && <ColumnRight>
          <ListTitle>Profile Fields</ListTitle>
          <ToggleList>
            <WizardToggle
              label="Previous Parish"
              icon="fa-church"
              checked={info.previousParish}
              onChange={(v) => updateField("studentInfo", "previousParish", v)}
              modeValue={modes.previousParish}
              onModeChange={(v) => setMode("previousParish", v)}
              alt={false}
              compact
            />
            <WizardToggle
              label="Preferred Language"
              icon="fa-language"
              checked={info.preferredLanguage}
              onChange={(v) => updateField("studentInfo", "preferredLanguage", v)}
              modeValue={modes.preferredLanguage}
              onModeChange={(v) => setMode("preferredLanguage", v)}
              alt
              compact
            />
            <WizardToggle
              label="Godparents / Sponsors"
              icon="fa-hand-holding-heart"
              checked={info.godparents}
              onChange={(v) => updateField("studentInfo", "godparents", v)}
              modeValue={modes.godparents}
              onModeChange={(v) => setMode("godparents", v)}
              alt={false}
              compact
            />
            <WizardToggle
              label="Siblings in Program"
              icon="fa-people-group"
              checked={info.siblings}
              onChange={(v) => updateField("studentInfo", "siblings", v)}
              modeValue={modes.siblings}
              onModeChange={(v) => setMode("siblings", v)}
              alt
              compact
            />
            <WizardToggle
              label="Special Needs / Accommodations"
              icon="fa-universal-access"
              checked={info.specialNeeds}
              onChange={(v) => updateField("studentInfo", "specialNeeds", v)}
              modeValue={modes.specialNeeds}
              onModeChange={(v) => setMode("specialNeeds", v)}
              alt={false}
              compact
            />
            <WizardToggle
              label="Parent Volunteer Interest"
              icon="fa-hands-helping"
              checked={info.parentVolunteer}
              onChange={(v) => updateField("studentInfo", "parentVolunteer", v)}
              modeValue={modes.parentVolunteer}
              onModeChange={(v) => setMode("parentVolunteer", v)}
              alt
              compact
            />
            <WizardToggle
              label="Mass Attendance"
              icon="fa-calendar-check"
              checked={info.massAttendance}
              onChange={(v) => updateField("studentInfo", "massAttendance", v)}
              modeValue={modes.massAttendance}
              onModeChange={(v) => setMode("massAttendance", v)}
              alt={false}
              compact
            />
            <WizardToggle
              label="Transportation Needs"
              icon="fa-car"
              checked={info.transportationNeeds}
              onChange={(v) => updateField("studentInfo", "transportationNeeds", v)}
              modeValue={modes.transportationNeeds}
              onModeChange={(v) => setMode("transportationNeeds", v)}
              alt
              last
              compact
            />
          </ToggleList>
        </ColumnRight>}
      </TwoColumnGrid>
    </Content>
  );
}

export default function OtherInfo({ subStep }) {
  if (subStep === 0) return <GeneralInfoPane />;
  if (subStep === 1) return <BaptismPane />;
  if (subStep === 2) return <MedicalEmergencyPane />;
  if (subStep === 4) return <CustomFieldsView />;
  return <SummaryView />;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`;

const ListTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  text-align: center;
`;

const FormCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const TwoColumnGrid = styled.div`
  flex: 1;
  display: ${(p) => (p.$single ? "flex" : "grid")};
  grid-template-columns: ${(p) => (p.$single ? "none" : "1fr 1fr")};
  ${(p) => p.$single && "justify-content: center;"}
  gap: 32px;
  align-items: start;
  align-content: center;
`;

const ColumnLeft = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${(p) => p.$single && "width: 100%; max-width: 500px;"}
`;

const ColumnRight = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Title = styled.h2`
  font-size: 32px;
  margin: 0;
  color: #182941;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 4px 0 0 0;
`;

const ToggleList = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  width: 100%;
`;

/* Custom fields styles */

const CustomFieldsSection = styled.div`
  width: 100%;
`;

const CFList = styled.div`
  border-radius: 20px;
  overflow: ${(p) => (p.$dragging ? "visible" : "hidden")};
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const CFRow = styled.div`
  background: ${(p) => p.$dragging ? "#fff" : p.$pressing ? "rgba(0, 0, 0, 0.06)" : p.$alt ? "#fff" : "#f5f5f5"};
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  user-select: none;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transform: ${(p) => p.$dragging ? `rotate(${p.$dragDir * 1}deg)` : p.$pressing ? "scale(0.98)" : "none"};
  transform-origin: 95% 25px;
  transition: background 0.15s, box-shadow 0.2s, border-radius 0.2s, transform 0.15s;
  box-shadow: ${(p) => (p.$dragging ? "0 8px 24px rgba(0, 0, 0, 0.18)" : "none")};
  border-radius: ${(p) => (p.$first ? "20px 20px 0 0" : "0")};

  &:hover {
    background: ${(p) => (p.$dragging ? "#fff" : "rgba(0, 0, 0, 0.04)")};
  }

  &:hover ${() => CFDeleteButton} {
    opacity: ${(p) => (p.$dragging ? 0 : 1)};
  }
`;

const CFIconCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(1, 96, 153, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 13px;
    color: ${ACCENT_COLOR};
  }
`;

const CFLabelWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CFLabel = styled.div`
  font-size: 15px;
  color: #182941;
`;

const CFTypeBadge = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 8px;
  border-radius: 100px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const CFHandleArea = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s;
  flex-shrink: 0;
`;

const CFDragHandle = styled.div`
  color: #9ca3af;
  font-size: 14px;
  cursor: grab;
  padding: 3px 6px;
  margin-right: -6px;
  flex-shrink: 0;
  height: 30px;
  align-self: flex-start;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: #6b7280;
  }

  &:active {
    cursor: grabbing;
  }
`;

const CFDeleteButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: #9ca3af;
  font-size: 13px;
  cursor: ${(p) => (p.$ready ? "pointer" : "default")};
  padding: 3px 6px;
  border-radius: 4px;
  opacity: 0;
  flex-shrink: 0;
  transition: color 0.2s, transform 0.15s, opacity 0.2s;

  &:hover {
    color: ${(p) => (p.$ready ? "#ef4444" : "#9ca3af")};
  }

  &:active {
    transform: ${(p) => (p.$ready ? "scale(0.8)" : "none")};
  }
`;

const CFWrapArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const CFLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CFActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const CFModePill = styled.button`
  appearance: none;
  border: none;
  border-radius: 100px;
  padding: 0;
  width: 62px;
  height: 22px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  color: ${(p) => (p.$require ? ACCENT_COLOR : "#6b7280")};
  background: ${(p) => (p.$require ? "rgba(1, 96, 153, 0.12)" : "#e5e5e5")};
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;
  transition: background 0.2s, color 0.15s;

  &:hover {
    background: ${(p) => (p.$require ? "rgba(1, 96, 153, 0.18)" : "#dedede")};
  }

  &:active ${() => CFSliderWrap} {
    transform: scale(0.9);
  }
`;

const CFSliderWrap = styled.div`
  position: relative;
  overflow: hidden;
  height: 22px;
  width: 100%;
  transition: transform 0.15s;

  span {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CFAddRow = styled.div`
  padding: 14px 16px;
  background: ${(p) => (p.$alt ? "#fff" : "#f5f5f5")};
  font-size: 15px;
  font-family: inherit;
  color: ${ACCENT_COLOR};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &:active {
    background: rgba(0, 0, 0, 0.08);
  }
`;

const CFAddInner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  transition: transform 0.15s;

  ${CFAddRow}:active & {
    transform: scale(0.97);
  }
`;

/* Summary styles */

const SummaryCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const SummaryIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(1, 96, 153, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SummaryIcon = styled.i`
  font-size: 18px;
  color: ${ACCENT_COLOR};
`;

const SummaryText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #182941;
`;

const SummarySentence = styled.div`
  font-size: 15px;
  color: #6b7280;
  line-height: 1.45;
`;

const AddMoreWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const AddMoreButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: ${ACCENT_COLOR};
  font-size: 17px;
  font-family: inherit;
  cursor: pointer;
  padding: 10px 20px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e5e7eb;
  }
`;

const EditButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: ${ACCENT_COLOR};
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  padding: 6px 12px 6px 10px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e5e7eb;
  }

  &:active {
    background: ${ACCENT_COLOR};
    color: #fff;
  }
`;

const EditButtonInner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  transition: transform 0.15s;

  ${EditButton}:active & {
    transform: scale(0.88);
  }
`;


/* Choice cards (mirrors PermissionRelease pattern) */

const ChoiceRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 500px;
`;

const ChoiceRow = styled.div`
  display: flex;
  align-items: center;
  padding: 22px 20px;
  background: ${(p) => (p.$selected ? "rgba(1, 96, 153, 0.04)" : "#fff")};
  border: 2px solid ${(p) => (p.$selected ? ACCENT_COLOR : "#e5e7eb")};
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: border-color 0.2s, box-shadow 0.2s, background 0.15s;
  user-select: none;

  &:hover {
    border-color: ${ACCENT_COLOR};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }

  &:active {
    background: #f7f7f8;
  }

  &:active ${() => ChoiceRowInner} {
    transform: scale(0.97);
  }
`;

const ChoiceRowInner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  transition: transform 0.15s;
`;

const ChoiceIcon = styled.i`
  font-size: 38px;
  flex-shrink: 0;
  color: ${ACCENT_COLOR};
`;

const ChoiceLabel = styled.div`
  font-size: 22px;
  color: #182941;
`;

const CheckIcon = styled.i`
  font-size: 38px;
  color: #00B44E;
  margin-left: auto;
  flex-shrink: 0;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.2s;
`;
